import { Visibility, VisibilityOff } from '@mui/icons-material';
import LockResetIcon from '@mui/icons-material/LockReset';
import {
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { validatePassword } from '../../../../../../corelogic/domain/utils/passwordValidation.utils';
import { changePassword } from '../../../../../../corelogic/state/authentication/authentication.action';
import { CHANGE_PASSWORD_SUCCESS } from '../../../../../../corelogic/state/authentication/authentication.action.type';
import { getAuthenticationError } from '../../../../../../corelogic/state/authentication/authentication.selector';
import { store } from '../../../../../../corelogic/state/store';
import { useError } from '../../../hooks/useError';
import { ConfirmationModal } from '../../ConfirmationModal/ConfirmationModal';
import { ErrorToast } from '../../Shared/Error/ErrorToast';
import { SuccessModal } from '../../Shared/SuccessModal/SuccessModal';

// Define all styles in one place
const styles = {
  textField: {
    '& .MuiInputBase-input': {
      color: 'white',
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
      '&.Mui-focused': {
        color: '#8B0000',
      },
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.23)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.5)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#8B0000',
      },
    },
    '& .MuiIconButton-root': {
      color: 'rgba(255, 255, 255, 0.7)',
      '&:hover': {
        color: 'white',
      },
    },
    marginBottom: 1,
  },
  redButton: {
    backgroundColor: '#8B0000',
    color: 'white',
    px: 3,
    py: 1,
    fontWeight: 500,
    width: '220px',
    height: '48px',
    gap: '5px',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.4)',
    '&:hover': {
      backgroundColor: '#aa0000',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
      transform: 'translateY(-1px)',
    },
    '&:active': {
      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.5)',
      transform: 'translateY(1px)',
    },
  },
  cancelButton: {
    color: 'rgba(255, 255, 255, 0.7)',
    '&:hover': {
      color: 'white',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 2,
  },
};

interface ChangePasswordProps {
  token: string;
  isUserLoading: boolean;
  onEditingChange: (isEditing: boolean) => void;
}

interface PasswordErrors {
  oldPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

interface PasswordVisibility {
  oldPassword: boolean;
  newPassword: boolean;
  confirmPassword: boolean;
}

export const ChangePassword: React.FC<ChangePasswordProps> = ({
  token,
  isUserLoading,
  onEditingChange,
}) => {
  // States
  const [isEditing, setIsEditing] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<PasswordErrors>({});
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showPassword, setShowPassword] = useState<PasswordVisibility>({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const isAuthLoading = store.getState().authentication.isLoading;
  const isLoading = isAuthLoading || isUserLoading;

  // Error handling
  const {
    error: authError,
    isVisible: authVisible,
    hideError: hideAuthError,
  } = useError(getAuthenticationError, 'authentication');

  // Form dirty check used to show confirmation dialog when dirty
  const isDirty = oldPassword || newPassword || confirmPassword;

  // Reset form for success or cancel
  const resetForm = useCallback(() => {
    setIsEditing(false);
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setFieldErrors({});
    onEditingChange(false);
  }, [onEditingChange]);

  // Listen for successful password change to reset form & show success modal
  useEffect(() => {
    const subscription = store.action$.subscribe((action) => {
      if (action.type === CHANGE_PASSWORD_SUCCESS) {
        setShowSuccessDialog(true);
        resetForm();
      }
    });

    return () => subscription.unsubscribe();
  }, [resetForm]);

  // Update parent when editing state changes to stir visual feedback
  useEffect(() => {
    onEditingChange(isEditing);
  }, [isEditing, onEditingChange]);

  // Validate fields on change to show error messages
  const validateField = useCallback(
    (field: keyof PasswordErrors, value: string) => {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };

        switch (field) {
          case 'oldPassword':
            newErrors.oldPassword = !value
              ? 'Current password is required'
              : undefined;
            break;

          case 'newPassword':
            const validation = validatePassword(value);
            newErrors.newPassword = value
              ? validation.isValid
                ? undefined
                : validation.error
              : undefined;
            if (confirmPassword) {
              newErrors.confirmPassword =
                value === confirmPassword ? undefined : "Passwords don't match";
            }
            break;

          case 'confirmPassword':
            newErrors.confirmPassword = value
              ? value === newPassword
                ? undefined
                : "Passwords don't match"
              : undefined;
            break;
        }

        return newErrors;
      });
    },
    [newPassword, confirmPassword],
  );

  // Handle cancel button to show confirmation dialog or reset form
  const handleCancel = useCallback(() => {
    if (isDirty) {
      setShowConfirmDialog(true);
    } else {
      resetForm();
    }
  }, [isDirty, resetForm]);

  // Handle submit button to validate fields & change password
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      console.log('Form submitted');

      validateField('oldPassword', oldPassword);
      validateField('newPassword', newPassword);
      validateField('confirmPassword', confirmPassword);

      if (Object.values(fieldErrors).some((error) => error !== undefined)) {
        return;
      }

      store.dispatch(
        changePassword({
          oldPassword,
          newPassword,
          token,
          retry: 2,
        }),
      );
    },
    [
      oldPassword,
      newPassword,
      confirmPassword,
      token,
      validateField,
      fieldErrors,
    ],
  );

  // Toggle password visibility
  const togglePasswordVisibility = (field: keyof PasswordVisibility) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  return (
    <>
      {/* Success Toast */}
      <SuccessModal
        isOpen={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title="Success"
        message="Password successfully changed!"
        autoHideDuration={5000}
      />

      {/* Error Toast for backend errors */}
      {authError && authVisible && (
        <ErrorToast
          message={authError}
          isVisible={true}
          onClose={hideAuthError}
        />
      )}

      {/* Render change password button when not editing */}
      {!isEditing ? (
        <Button
          variant="contained"
          color="primary"
          startIcon={<LockResetIcon />}
          onClick={() => setIsEditing(true)}
          sx={styles.redButton}
        >
          Change Password
        </Button>
      ) : (
        <>
          {/* Render password form when editing */}
          <form onSubmit={handleSubmit}>
            <Stack
              spacing={2}
              sx={{ mt: 2 }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  handleCancel();
                }
              }}
            >
              <TextField
                label="Current Password"
                type={showPassword.oldPassword ? 'text' : 'password'}
                fullWidth
                value={oldPassword}
                onChange={(e) => {
                  setOldPassword(e.target.value);
                  validateField('oldPassword', e.target.value);
                }}
                required
                error={!!fieldErrors.oldPassword}
                helperText={fieldErrors.oldPassword}
                disabled={isLoading}
                sx={styles.textField}
                InputProps={{
                  endAdornment: (
                    <>
                      {isLoading && (
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                      )}
                      <IconButton
                        onClick={() => togglePasswordVisibility('oldPassword')}
                        edge="end"
                      >
                        {showPassword.oldPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </>
                  ),
                }}
              />
              <TextField
                label="New Password"
                type={showPassword.newPassword ? 'text' : 'password'}
                fullWidth
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  validateField('newPassword', e.target.value);
                }}
                required
                error={!!fieldErrors.newPassword}
                helperText={
                  fieldErrors.newPassword ||
                  (newPassword.length >= 6 ? (
                    <span style={{ color: '#4caf50' }}>
                      ✓ Password meets requirements
                    </span>
                  ) : (
                    <span style={{ color: '#f44336' }}>
                      Password must be at least 6 characters
                    </span>
                  ))
                }
                color={newPassword.length >= 6 ? 'success' : undefined}
                disabled={isLoading}
                sx={styles.textField}
                InputProps={{
                  endAdornment: (
                    <>
                      {isLoading && (
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                      )}
                      <IconButton
                        onClick={() => togglePasswordVisibility('newPassword')}
                        edge="end"
                      >
                        {showPassword.newPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </>
                  ),
                }}
              />
              <TextField
                label="Confirm New Password"
                type={showPassword.confirmPassword ? 'text' : 'password'}
                fullWidth
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  validateField('confirmPassword', e.target.value);
                }}
                required
                error={!!fieldErrors.confirmPassword}
                helperText={
                  fieldErrors.confirmPassword ||
                  (confirmPassword === newPassword ? (
                    <span style={{ color: '#4caf50' }}>✓ Passwords match</span>
                  ) : (
                    <span style={{ color: '#f44336' }}>
                      Passwords don't match
                    </span>
                  ))
                }
                color={
                  confirmPassword === newPassword && confirmPassword !== ''
                    ? 'success'
                    : undefined
                }
                disabled={isLoading}
                sx={styles.textField}
                InputProps={{
                  endAdornment: (
                    <>
                      {isLoading && (
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                      )}
                      <IconButton
                        onClick={() =>
                          togglePasswordVisibility('confirmPassword')
                        }
                        edge="end"
                      >
                        {showPassword.confirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </>
                  ),
                }}
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  onClick={handleCancel}
                  disabled={isLoading}
                  sx={styles.cancelButton}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={
                    isLoading ||
                    !oldPassword ||
                    !newPassword ||
                    !confirmPassword ||
                    Object.values(fieldErrors).some(
                      (error) => error !== undefined,
                    )
                  }
                  sx={styles.redButton}
                >
                  {isLoading ? 'Changing Password...' : 'Change Password'}
                </Button>
              </Stack>
            </Stack>
          </form>

          {/* Confirmation Dialog for Discarding Changes */}
          <ConfirmationModal
            isOpen={showConfirmDialog}
            onClose={() => setShowConfirmDialog(false)}
            onConfirm={() => {
              setShowConfirmDialog(false);
              resetForm();
            }}
            title="Discard Changes"
            message="Are you sure you want to discard your changes?"
          />
        </>
      )}
    </>
  );
};
